import React, { useEffect, useRef, useState } from 'react';

import { FiberManualRecord } from '@material-ui/icons';
import styled, { css } from 'styled-components';
import {
  FlexContainer,
  NewStyledIconButton,
  StyledBox,
  StyledDivider,
  StyledGrid,
  StyledLinearProgress,
  StyledTypography,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import { GOAL_PROGRESS_OPTIONS_MAP } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getColorFromKey, getColorFromKeyForAnalytics } from 'constants/helper';

import { usePopperState } from '../screens/analytics/popperContext';
import useOrganisationSettings from './hooks/useOrganisationSettings';

export const HoverAbleContainerComp = ({ children, displayInfoComp, calibrateAble = true, hoverAble = true }) => {
  const { showPopper, hidePopper } = usePopperState();

  const componentElem = useRef(null);

  const handleMouseOver = () => {
    showPopper({
      anchorEl: componentElem.current,
      onClose: () => {},
      component: displayInfoComp,
    });
  };

  const handleMouseLeave = () => {
    hidePopper();
  };

  return (
    <HoverAbleContainer
      ref={componentElem}
      $calibrateAble={calibrateAble}
      $hoverAble={hoverAble}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </HoverAbleContainer>
  );
};

export const MetricInfoView = ({ objectiveName = 'Brand Recall', value, showProgressBar = true }) => {
  const { AT_RISK: atRisKObj, CLOSED: closedObj, OFF_TRACK: offTrackObj, ON_TRACK: onTrackObj, COUNT } = value;

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const goalProgressStats = [
    {
      displayName: GOAL_PROGRESS_OPTIONS_MAP.ON_TRACK.label,
      count: onTrackObj.COUNT,
      completionPercentage: onTrackObj.COMPLETED_PERCENTAGE,
      color: getColorFromKey(GOAL_PROGRESS_OPTIONS_MAP.ON_TRACK.color),
    },
    {
      displayName: GOAL_PROGRESS_OPTIONS_MAP.OFF_TRACK.label,
      count: offTrackObj.COUNT,
      completionPercentage: offTrackObj.COMPLETED_PERCENTAGE,
      color: getColorFromKey(GOAL_PROGRESS_OPTIONS_MAP.OFF_TRACK.color),
    },
    {
      displayName: GOAL_PROGRESS_OPTIONS_MAP.AT_RISK.label,
      count: atRisKObj.COUNT,
      completionPercentage: atRisKObj.COMPLETED_PERCENTAGE,
      color: getColorFromKey(GOAL_PROGRESS_OPTIONS_MAP.AT_RISK.color),
    },
    {
      displayName: GOAL_PROGRESS_OPTIONS_MAP.CLOSED.label,
      count: closedObj.COUNT,
      completionPercentage: closedObj.COMPLETED_PERCENTAGE,
      color: getColorFromKey('primary'),
    },
  ];

  return (
    <StyledBox padding="1rem" width={showProgressBar ? '40ch' : '30ch'}>
      <StyledGrid container alignItems="center" justify="space-between">
        <StyledTypography variant="body1" weight={600}>
          {objectiveName}
        </StyledTypography>
        <StyledTypography variant="body1" color="textSecondary">
          {`${COUNT} ${COUNT === 1 ? keyResultsName : modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)}`}
        </StyledTypography>
      </StyledGrid>

      <StyledDivider bottomGutter={2} topGutter={2} />
      {goalProgressStats.map(({ displayName, count, completionPercentage, color }) => (
        <>
          {showProgressBar ? (
            <StatRowWithProgressBar
              displayName={displayName}
              count={count}
              completionPercentage={completionPercentage}
              color={color}
            />
          ) : (
            <StatRow
              displayName={displayName}
              count={count}
              completionPercentage={completionPercentage}
              color={color}
            />
          )}
        </>
      ))}
    </StyledBox>
  );
};

export const MetricInfoViewForGoalsRevamp = ({ objectiveName = '', value, showProgressBar = true, overall = {} }) => {
  const {
    AT_RISK: atRisKObj,
    CLOSED: closedObj,
    OFF_TRACK: offTrackObj,
    ON_TRACK: onTrackObj,
    // COUNT
  } = value;

  const [goalProgressStats, setGoalProgressStats] = useState([]);

  const addGoalsProgressDataToArray = () => {
    const arr = [];

    Object.keys(value).forEach((key) => {
      if (value[key]) {
        arr.push({
          displayName: GOAL_PROGRESS_OPTIONS_MAP[key].label,
          count: value[key].count,
          completionPercentage: value[key].avgCompletionPercentage,
          color: getColorFromKey(GOAL_PROGRESS_OPTIONS_MAP[key].color),
          order: GOAL_PROGRESS_OPTIONS_MAP[key].order,
        });
      }
    });
    const sortedArray = arr.sort((a, b) => a?.order - b?.order);

    setGoalProgressStats(sortedArray);
  };

  useEffect(() => {
    addGoalsProgressDataToArray();
  }, []);

  return (
    <StyledBox padding="1rem" width={showProgressBar ? '40ch' : '30ch'}>
      <StyledGrid container alignItems="center" justify="space-between">
        <StyledTypography variant="body1" color="textSecondary">
          Status-wise Progress
        </StyledTypography>
      </StyledGrid>

      <StyledDivider style={{ marginLeft: '-1rem', marginRight: '-1rem' }} bottomGutter={2} topGutter={2} />
      {goalProgressStats.map(({ displayName, count, completionPercentage, color }) => (
        <>
          {showProgressBar ? (
            <StatRowWithProgressBarForGoalsRevamp
              displayName={displayName}
              count={count}
              completionPercentage={completionPercentage}
              color={color}
            />
          ) : (
            <StatRow
              displayName={displayName}
              count={count}
              completionPercentage={completionPercentage}
              color={color}
            />
          )}
        </>
      ))}
    </StyledBox>
  );
};

const StatRowWithProgressBarForGoalsRevamp = ({ displayName, count, completionPercentage, color }) => (
  <FlexContainer style={{ flexDirection: 'column', padding: '0.125rem' }}>
    <VerticallyAlignedFlexContainer>
      {displayName}{' '}
      <StyledTypography variant="body1" color="textSecondary">
        : {count}
      </StyledTypography>
      <StyledTypography style={{ marginLeft: 'auto' }} variant="body2" color={color}>
        {`${Math.round(completionPercentage)}%`}
      </StyledTypography>
    </VerticallyAlignedFlexContainer>
    <FlexContainer>
      <StyledLinearProgress
        variant="determinate"
        color="primary"
        barColor={color}
        value={+completionPercentage === 0 ? 1 : completionPercentage}
        width="100%"
        style={{ marginRight: '0' }}
      />
    </FlexContainer>
    <StyledDivider style={{ marginLeft: '-1rem', marginRight: '-1rem' }} bottomGutter={2} topGutter={2} />
  </FlexContainer>
);

const StatRowWithProgressBar = ({ displayName, count, completionPercentage, color }) => (
  <StyledGrid container bottomGutter={1}>
    <StyledGrid item xs={4}>
      {displayName}
    </StyledGrid>
    <StyledGrid item xs={2}>
      <StyledTypography variant="body1" color="textSecondary">
        {count}
      </StyledTypography>
    </StyledGrid>
    <StyledGrid container alignItems="center" item xs={5}>
      <StyledLinearProgress variant="determinate" color="primary" barColor={color} value={completionPercentage} />
    </StyledGrid>
    <StyledGrid container alignItems="center" justify="flex-end" item xs={1}>
      <StyledTypography variant="body2" color={color}>
        {`${completionPercentage}%`}
      </StyledTypography>
    </StyledGrid>
  </StyledGrid>
);

export const DonutChartInfoView = ({ objectiveName, value }) => (
  <MetricInfoView objectiveName={objectiveName} value={value} showProgressBar={false} />
);

const StatRow = ({ displayName, count, color }) => (
  <StyledGrid container bottomGutter={1}>
    <StyledGrid item xs={9}>
      <NewStyledIconButton size="header" color={getColorFromKeyForAnalytics(color)}>
        <FiberManualRecord />
      </NewStyledIconButton>
      {displayName}
    </StyledGrid>
    <StyledGrid container justify="flex-end" item xs={3}>
      <StyledTypography variant="body1" color="textSecondary">
        {count}
      </StyledTypography>
    </StyledGrid>
  </StyledGrid>
);

const HoverAbleContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  background-color: ${(props) => props.isEditing && props.theme.palette.background.lightBlue};

  ${(props) =>
    props.$calibrateAble &&
    css`
      &:hover {
        cursor: pointer;
        background-color: ${(props) => props.theme.palette.background.lightBlue};
      }
    `};
  ${(props) =>
    props.$hoverAble &&
    css`
      &:hover {
        background-color: ${(props) => props.theme.palette.background.lightBlue};
      }
    `};
`;
